// How a `seller_threads.decline_reason` value (migration 0018) prints, and the
// order the reasons read in.
//
// Presentation ONLY. There is no counting, bucketing, or rate math here and there
// must not be: a breakdown BY reason is a metric, metrics come from a versioned
// Dataform view in curated BigQuery, and a second definition assembled in the
// browser would disagree with Superset the first time either side changed what
// counts. `lib/talent.ts` is the one place aggregation lives, and it folds the BI
// snapshot, never product rows.

/** Friendly labels for the enum the backend CHECK constraint currently allows.
 *
 *  A Map, not an object literal, and deliberately so: the key is a reason string
 *  arriving off the wire, and `LABELS["constructor"]` on a plain object resolves up
 *  the prototype chain to `Object.prototype.constructor` — a FUNCTION, which is not
 *  nullish, so a `?? value` fallback would hand React a function to render instead
 *  of falling back. A Map has no prototype keys, so an unknown reason is simply
 *  unknown. */
const REASON_LABELS = new Map<string, string>([
  ["not_available", "Not available"],
  ["out_of_scope", "Out of scope"],
  ["budget_too_low", "Budget too low"],
  ["timeline_too_tight", "Timeline too tight"],
  ["other", "Other"],
]);

/**
 * A decline reason as it prints.
 *
 * Falls back to the RAW value for anything unmapped, which is the whole point: the
 * filter's options are derived from the rows, so a reason the backend adds to the
 * CHECK constraint before this bundle ships appears here unlabelled rather than
 * vanishing from the control and hiding every row that carries it. A `?? value`
 * fallback is uglier than a tidy label and strictly more honest.
 */
export function declineReasonLabel(value: string): string {
  return REASON_LABELS.get(value) ?? value;
}

/**
 * Fixed option ORDER, worst-signal first.
 *
 * Not alphabetical and not frequency-ordered (frequency would be a count, which
 * this file does not do). The two reasons that mean we mispriced or misread the
 * brief lead, because those are the ones an operator can act on; "not available"
 * and "other" are noise by comparison. Values outside this list follow it, ordered
 * by label, per `FilterColumn.order`.
 */
export const DECLINE_REASON_ORDER: readonly string[] = [
  "budget_too_low",
  "timeline_too_tight",
  "out_of_scope",
  "not_available",
  "other",
];
